import React from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { getFullTime } from '../shared/utils/date';
import { tournamentSetScore } from '../shared/utils/tournament-score';
import { storageImage } from '../shared/utils/storage-image';

export default function MatchSummaryBox({
  match,
  offering,
  offeringPartner,
  opponent,
  opponentPartner,
  courtName,
  className,
  isPartner,
  pendingScores,
  dateTime,
}: {
  match: Tournaments.Game | Matches.Match;
  offering?: Users.User;
  offeringPartner?: Users.User;
  opponent?: Users.User;
  opponentPartner?: Users.User;
  courtName: string;
  className: string;
  isPartner?: boolean;
  pendingScores?: boolean;
  dateTime?: string;
}) {
  const { t } = useTranslation('match');
  const score = (value: number | undefined) => (pendingScores ? '—' : value ?? '—');
  // Unknown entrants read "TBD"/"—" instead of a bare "?" (docs/UI-UX-SYSTEM.md §1.4).
  const fullName = (user?: Users.User) => (user?.ad ? `${user.ad} ${user.soyad}` : '');
  const offeringName = fullName(offering) || t('summary.playerTbd');
  const offeringPartnerName = fullName(offeringPartner) || '—';
  const opponentName = fullName(opponent) || t('summary.playerTbd');
  const opponentPartnerName = fullName(opponentPartner) || '—';
  // Stored placeholders such as "?" or "? 21:00" mean the date is not set yet.
  const matchTime = (
    dateTime ??
    (typeof match.mac_saati === 'string' ? match.mac_saati : getFullTime(match.mac_saati))
  )
    .replace(/^\?\s*/, '')
    .trim();

  return (
    <div
      className={`match-summary-box ${className}`}
      role="group"
      aria-label={t('summary.ariaMatch', { first: offeringName, second: opponentName })}
    >
      <div className="match-summary-box-info">
        <span>{matchTime || t('summary.dateTbd')}</span>
        <span title={courtName}>{courtName || t('summary.courtTbd')}</span>
      </div>
      <div className="match-summary-box-row">
        <div className="match-summary-box-player" />
        <div className="match-summary-box-scores">
          <div className="match-summary-box-score match-summary-box-score--title">
            {t('summary.matchLabel')}
          </div>
          <div className="match-summary-box-score match-summary-box-score--title">1</div>
          <div className="match-summary-box-score match-summary-box-score--title">2</div>
          <div className="match-summary-box-score match-summary-box-score--title">3</div>
        </div>
      </div>
      <div className="match-summary-box-row">
        <div>
          <div className="match-summary-box-player">
            <Avatar
              src={storageImage(offering?.foto_mini_url, 24)}
              size={24}
              icon={<UserOutlined />}
            />
            <span className="match-summary-box-player-name" title={offeringName}>
              {offeringName}
            </span>
          </div>
          {isPartner ? (
            <div className="match-summary-box-player-partner">
              <Avatar
                src={storageImage(offeringPartner?.foto_mini_url, 18)}
                size={18}
                icon={<UserOutlined />}
              />
              <span className="match-summary-box-player-name" title={offeringPartnerName}>
                {offeringPartnerName}
              </span>
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="match-summary-box-scores">
          <div className="match-summary-box-score">{score(match.kul_t_sonuc)}</div>
          <div className="match-summary-box-score">
            {score(tournamentSetScore(match, 'T', 1) ?? undefined)}
          </div>
          <div className="match-summary-box-score">
            {score(tournamentSetScore(match, 'T', 2) ?? undefined)}
          </div>
          <div className="match-summary-box-score">
            {score(tournamentSetScore(match, 'T', 3) ?? undefined)}
          </div>
        </div>
      </div>
      <hr className="match-summary-box-divider" />
      <div className="match-summary-box-row">
        <div>
          <div className="match-summary-box-player">
            <Avatar
              src={storageImage(opponent?.foto_mini_url, 24)}
              size={24}
              icon={<UserOutlined />}
            />
            <span className="match-summary-box-player-name" title={opponentName}>
              {opponentName}
            </span>
          </div>
          {isPartner ? (
            <div className="match-summary-box-player-partner">
              <Avatar
                src={storageImage(opponentPartner?.foto_mini_url, 18)}
                size={18}
                icon={<UserOutlined />}
              />
              <span className="match-summary-box-player-name" title={opponentPartnerName}>
                {opponentPartnerName}
              </span>
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="match-summary-box-scores">
          <div className="match-summary-box-score">{score(match.kul_r_sonuc)}</div>
          <div className="match-summary-box-score">
            {score(tournamentSetScore(match, 'R', 1) ?? undefined)}
          </div>
          <div className="match-summary-box-score">
            {score(tournamentSetScore(match, 'R', 2) ?? undefined)}
          </div>
          <div className="match-summary-box-score">
            {score(tournamentSetScore(match, 'R', 3) ?? undefined)}
          </div>
        </div>
      </div>
    </div>
  );
}

MatchSummaryBox.defaultProps = {
  dateTime: undefined,
  pendingScores: false,
  offering: undefined,
  offeringPartner: undefined,
  opponent: undefined,
  opponentPartner: undefined,
  isPartner: false,
};
